import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import {
  Search, Printer, Tag, AlertTriangle, Sparkles, ClipboardList, ArrowLeft, FileSignature, Download,
} from 'lucide-react';
import Header from '../components/Header';
import { SECTIONS } from '../lib/sectionTheme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const P = SECTIONS.preparation;

// Tag → {label, color, description} — reception needs to see at a glance.
const TAG_META = {
  'CT-CONTRAST-RISK':         { label: 'Contrast risk', color: '#DC2626' },
  'METFORMIN':                { label: 'Metformin check', color: '#D97706' },
  'RENAL-FUNCTION-REQUIRED':  { label: 'eGFR required', color: '#DC2626' },
  'THYROID-CHECK':            { label: 'Thyroid check', color: '#D97706' },
  'BLOOD-THINNER':            { label: 'Blood thinner', color: '#DC2626' },
  'DIABETIC-STEROID-WARNING': { label: 'Diabetic steroid', color: '#D97706' },
  'DRIVER-REQUIRED':          { label: 'Driver required', color: '#7C3AED' },
  'FASTING':                  { label: 'Fasting', color: '#0891B2' },
  'FULL-BLADDER':             { label: 'Full bladder', color: '#0891B2' },
  'PREGNANCY-CHECK':          { label: 'Pregnancy check', color: '#EC4899' },
  'PROCEDURE-CONSENT':        { label: 'Consent', color: '#7C3AED' },
};

function tagStyle(tag) {
  const m = TAG_META[tag];
  const color = m?.color || '#64748B';
  return { backgroundColor: `${color}15`, color, borderColor: `${color}40` };
}

const MODALITY_ORDER = ['General', 'CT', 'Ultrasound', 'X-ray', 'DEXA', 'Dental', 'Special Patient Groups'];

export default function PreparationPage() {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [activeModality, setActiveModality] = useState('All');
  const [activeTag, setActiveTag] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios.get(`${API}/prep-guides`, { withCredentials: true })
      .then(r => setGuides(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const modalities = useMemo(() => {
    const set = new Set(guides.map(g => g.modality));
    return MODALITY_ORDER.filter(m => set.has(m)).concat([...set].filter(m => !MODALITY_ORDER.includes(m)));
  }, [guides]);

  const allTags = useMemo(() => {
    const set = new Set();
    guides.forEach(g => (g.alerts || []).forEach(t => set.add(t)));
    return [...set].sort();
  }, [guides]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return guides.filter(g => {
      if (activeModality !== 'All' && g.modality !== activeModality) return false;
      if (activeTag && !(g.alerts || []).includes(activeTag)) return false;
      if (ql && !`${g.title} ${g.subcategory} ${g.content}`.toLowerCase().includes(ql)) return false;
      return true;
    });
  }, [guides, q, activeModality, activeTag]);

  const grouped = useMemo(() => {
    const out = {};
    for (const g of filtered) {
      (out[g.modality] = out[g.modality] || []).push(g);
    }
    return out;
  }, [filtered]);

  if (selected) {
    return <PreparationDetail guide={selected} onBack={() => setSelected(null)} />;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="preparation-page">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4" style={{ color: P.color }} />
              <span className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: P.color }}>Patient preparation library</span>
            </div>
            <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
              Prep <span style={{ color: P.color }}>notes & forms</span>
            </h1>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl">
              Every exam and procedure, with fasting rules, booking alerts and special patient groups. Print any page for the patient.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            <a
              href={`${API}/prep-guides/download-all.pdf`}
              target="_blank"
              rel="noreferrer"
              data-testid="prep-download-all"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl border"
              style={{ borderColor: `${P.color}40`, color: P.color }}
            >
              <Download className="w-4 h-4" /> Download all (PDF)
            </a>
            <Link to="/screening" data-testid="prep-to-screening"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-xl"
              style={{ backgroundColor: P.color }}>
              <FileSignature className="w-4 h-4" /> Start patient screening
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-card border border-border rounded-2xl p-4 mb-6" data-testid="prep-filters">
          <div className="flex items-center gap-2 mb-3">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input
              type="search"
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search by exam, procedure, or keyword…"
              data-testid="prep-search"
              className="flex-1 bg-transparent border-0 outline-none text-sm text-foreground placeholder:text-muted-foreground"
            />
          </div>

          {/* Modality pills */}
          <div className="flex items-center gap-1.5 flex-wrap border-t border-border pt-3">
            <button
              onClick={() => setActiveModality('All')}
              data-testid="prep-modality-All"
              className="text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors"
              style={activeModality === 'All'
                ? { backgroundColor: P.colorBg, borderColor: P.ring, color: P.color }
                : { borderColor: 'hsl(var(--border))', color: 'hsl(var(--muted-foreground))' }}
            >
              All · {guides.length}
            </button>
            {modalities.map(m => {
              const count = guides.filter(g => g.modality === m).length;
              const active = activeModality === m;
              return (
                <button
                  key={m}
                  onClick={() => setActiveModality(m)}
                  data-testid={`prep-modality-${m.replace(/\s+/g, '-')}`}
                  className="text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors"
                  style={active
                    ? { backgroundColor: P.colorBg, borderColor: P.ring, color: P.color }
                    : { borderColor: 'hsl(var(--border))', color: 'hsl(var(--muted-foreground))' }}
                >
                  {m} · {count}
                </button>
              );
            })}
          </div>

          {/* Tag filter */}
          {allTags.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap border-t border-border pt-3 mt-3">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mr-1 inline-flex items-center gap-1">
                <Tag className="w-3 h-3" /> Alerts
              </span>
              <button
                onClick={() => setActiveTag('')}
                data-testid="prep-tag-clear"
                className="text-[10px] font-semibold px-2 py-1 rounded-full border"
                style={!activeTag
                  ? { backgroundColor: P.colorBg, borderColor: P.ring, color: P.color }
                  : { borderColor: 'hsl(var(--border))', color: 'hsl(var(--muted-foreground))' }}
              >
                Any
              </button>
              {allTags.map(t => {
                const meta = TAG_META[t];
                const active = activeTag === t;
                return (
                  <button
                    key={t}
                    onClick={() => setActiveTag(active ? '' : t)}
                    data-testid={`prep-tag-${t}`}
                    className="text-[10px] font-semibold px-2 py-1 rounded-full border"
                    style={active ? tagStyle(t) : { borderColor: 'hsl(var(--border))', color: 'hsl(var(--muted-foreground))' }}
                  >
                    {meta?.label || t}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* List */}
        {loading ? (
          <p className="p-6 text-center text-sm text-muted-foreground">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-border rounded-2xl p-12 text-center" data-testid="prep-empty">
            <p className="text-sm text-muted-foreground">No prep guides match that filter.</p>
          </div>
        ) : (
          <div className="space-y-8" data-testid="prep-list">
            {Object.entries(grouped).map(([modality, items]) => (
              <section key={modality} data-testid={`prep-section-${modality.replace(/\s+/g, '-')}`}>
                <h2 className="font-['Outfit'] text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                  <span>{modality}</span>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">· {items.length}</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {items.map(g => (
                    <button
                      key={g.guide_id}
                      onClick={() => setSelected(g)}
                      data-testid={`prep-card-${g.guide_id}`}
                      className="text-left p-4 bg-card border border-border rounded-2xl hover:shadow-sm transition-all hover:border-[--i3-teal] group"
                      style={{ '--i3-teal': P.color }}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <p className="font-['Outfit'] text-sm font-semibold text-foreground">{g.title}</p>
                        {g.is_custom && (
                          <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${P.color}15`, color: P.color }}>
                            Custom
                          </span>
                        )}
                      </div>
                      {g.subcategory && (
                        <p className="text-[11px] text-muted-foreground mb-2">{g.subcategory}</p>
                      )}
                      {(g.alerts || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(g.alerts || []).slice(0, 4).map(t => (
                            <span key={t} className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border" style={tagStyle(t)}>
                              {TAG_META[t]?.label || t}
                            </span>
                          ))}
                          {(g.alerts || []).length > 4 && (
                            <span className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                              +{(g.alerts || []).length - 4}
                            </span>
                          )}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PreparationDetail({ guide, onBack }) {
  const print = () => window.print();

  return (
    <div className="min-h-screen bg-background" data-testid="prep-detail">
      <div className="print:hidden"><Header /></div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:py-4">
        {/* Action bar */}
        <div className="flex items-center justify-between mb-6 print:hidden">
          <button
            onClick={onBack}
            data-testid="prep-detail-back"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4" /> Back to library
          </button>
          <button
            onClick={print}
            data-testid="prep-detail-print"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-xl"
            style={{ backgroundColor: P.color }}
          >
            <Printer className="w-4 h-4" /> Print / Save PDF
          </button>
        </div>

        {/* Print-only header with clinic branding */}
        <div className="hidden print:block mb-6 pb-4" style={{ borderBottom: '3px solid #1f4a2e' }}>
          <div style={{ height: 4, background: 'linear-gradient(90deg,#1f4a2e 0%,#3fa758 100%)', borderRadius: 2, marginBottom: 12 }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
            <img src="/brand/sp-logo.png" alt="Sanctuary Point Ultrasound" style={{ height: 56, width: 'auto', objectFit: 'contain' }} />
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: '13px', fontWeight: 600, color: '#1f4a2e', margin: 0, fontFamily: "'Museo Slab','Zilla Slab','Outfit',serif" }}>Patient Preparation</p>
              <p style={{ fontSize: '10px', marginTop: 4, color: '#3fa758', fontStyle: 'italic', letterSpacing: '0.14em', textTransform: 'uppercase', fontFamily: "'Museo Slab','Zilla Slab','Outfit',serif" }}>Sanctuary Point · Ultrasound · Radiology</p>
            </div>
          </div>
        </div>

        {/* Title card */}
        <div className="bg-card border border-border rounded-2xl p-6 mb-6 print:border-0 print:p-0">
          <div className="flex items-start justify-between gap-3 mb-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: P.color }}>
              {guide.modality}{guide.subcategory ? ` · ${guide.subcategory}` : ''}
            </span>
          </div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold tracking-tight text-foreground">{guide.title}</h1>

          {(guide.alerts || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-4" data-testid="prep-detail-tags">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Booking alerts:
              </span>
              {(guide.alerts || []).map(t => (
                <span
                  key={t}
                  className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border"
                  style={tagStyle(t)}
                  data-testid={`prep-detail-tag-${t}`}
                >
                  {TAG_META[t]?.label || t}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Markdown content */}
        <div className="bg-card border border-border rounded-2xl p-6 print:border-0 print:p-0 prep-markdown" data-testid="prep-detail-content">
          <ReactMarkdown>{guide.content || ''}</ReactMarkdown>
        </div>

        {/* Print footer */}
        <div className="hidden print:block mt-6 pt-4 border-t border-gray-400 text-[10px] text-gray-600">
          <p>Printed from SP Radiology Assistant · This sheet is a general guide. Please follow any personalised instructions from your referring clinician or the radiologist.</p>
        </div>
      </div>

      <style>{`
        .prep-markdown h2 { font-family: 'Outfit', sans-serif; font-weight: 600; font-size: 1.05rem; margin-top: 1.5rem; margin-bottom: 0.5rem; color: ${P.color}; }
        .prep-markdown h2:first-child { margin-top: 0; }
        .prep-markdown p { margin-bottom: 0.75rem; font-size: 0.92rem; line-height: 1.55; }
        .prep-markdown ul { margin-bottom: 1rem; padding-left: 1.25rem; }
        .prep-markdown li { margin-bottom: 0.3rem; font-size: 0.92rem; line-height: 1.5; list-style: disc; }
        .prep-markdown strong { font-weight: 600; color: hsl(var(--foreground)); }
        .prep-markdown code { background: hsl(var(--muted)); padding: 0.1rem 0.35rem; border-radius: 4px; font-size: 0.85em; }
        .prep-markdown blockquote { border-left: 3px solid ${P.color}; padding: 0.5rem 1rem; margin: 1rem 0; background: ${P.color}0D; font-style: italic; }
        @media print {
          .prep-markdown h2 { color: black; font-size: 1rem; }
          .prep-markdown blockquote { border-left-color: black; background: transparent; }
          body { background: white; }
        }
      `}</style>
    </div>
  );
}
